import { Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ClassPage from "./ClassPage";
import NotesPage from "./NotesPage";
import ExamPapersPage from "./ExamPapersPage";
import VideosPage from "./VideosPage";
import LinksPage from "./LinksPage";
import QuizzesPage from "./QuizzesPage";
import Tests from "./Tests";
import CalendarPage from "./CalendarPage";
import ClassAdminPage from "./ClassAdminPage";
import Cat4InsightsPage from "./Cat4InsightsPage";
import SeatingPlanPage from "./SeatingPlanPage";
import LiveQuizPage from "./LiveQuizPage";
import StudentJoinQuizPage from "./StudentJoinQuizPage";
import LoginPage from "./LoginPage";
import RegisterPage from "./RegisterPage";
import StudentClassPage from "./StudentClassPage";
import { apiFetch, clearDemoToken, clearToken, getToken } from "./api";
import WhiteBoardPage from "./WhiteBoardPage";
import TeacherAdminPage from "./TeacherAdminPage";
import ClassReportPage from "./ClassReportPage";
import StudentReportPage from "./StudentReportPage";
import TeacherPlanner from "./TeacherPlanner";
import CreateResources from "./CreateResources";
import AdminUsersPage from "./AdminUsersPage";
import StudentCollabRoomPage from "./StudentCollabRoomPage";
import CollaborationPage from "./CollaborationPage";
import LegalPage from "./LegalPage";
import ResetPasswordPage from "./ResetPasswordPage";
import VerifyEmailPage from "./VerifyEmailPage";
import StudentPage from "./StudentPage";
import StudentPracticeQuizPage from "./StudentPracticeQuizPage";
import StudentPracticeLibraryPage from "./StudentPracticeLibraryPage";
import StudentExamPapersPage from "./StudentExamPapersPage";
import PublicExamCollectionPage, { PublicExamLevelPage, PublicExamTopicPage } from "./PublicExamCollectionPage";
import BillingSuccessPage from "./BillingSuccessPage";
import BillingCancelPage from "./BillingCancelPage";
import BillingOnboardingPage from "./BillingOnboardingPage";
import ArchivedClassesPage from "./ArchivedClassesPage";
import Cat4DemoPage from "./Cat4DemoPage";
import DemoAccountEntryPage from "./DemoAccountEntryPage";
import PublicHomePage from "./PublicHomePage";
import SchoolAdminPage from "./SchoolAdminPage";
import SchoolResourcesPage from "./SchoolResourcesPage";
import SchoolInvitationPage from "./SchoolInvitationPage";
import PlatformAdminSchoolsPage from "./PlatformAdminSchoolsPage";
import PlatformAdminEntitlementsPage from "./PlatformAdminEntitlementsPage";
import SchoolBrand from "./Components/SchoolBrand";
import InlineNotice from "./Components/InlineNotice";
import { userFacingError } from "./userFacingError";
import LanguageSwitch from "./Components/LanguageSwitch";
import UiText from "./Components/UiText";
import { formatDashboardDateCard } from "./dashboardDateFormat";
import { dashboardCreateResourcesVisibilityClass } from "./dashboardPhoneGuard";
import { useUiLanguage } from "./i18n/UiLanguageContext";
import {
  CLASS_COLOUR_OPTIONS,
  DEFAULT_CLASS_COLOUR_KEY,
  classColourBackgroundClass,
  normaliseClassColourKey,
  resolveClassColourKey,
  type ClassColourKey,
} from "./classAppearance";
import {
  consumeCreateClassHandoff,
  shouldShowFirstClassPrompt,
  type ServerClassLoadStatus,
} from "./classOnboarding";


import ELogo2 from "./assets/ELogo2.png";
import PlannerLogo from "./assets/Planner_Logo.png";
import pilotUserBadge from "./assets/Elume Pilot User.png";


type ClassItem = {
  id: number;
  name: string;
  subject: string;
  stream?: string | null;
  color?: string | null;
  dashboard_order?: number | null;
  is_archived?: boolean;
  archived_at?: string | null;
};

const API_BASE = "/api";
export const CLASS_ORDER_SAVE_ERROR = "We couldn\u2019t save that class order just now. Please try again.";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const TEACHER_INSTALL_DISMISS_KEY = "elume:teacher:install-prompt:dismissed";

function isStandaloneApp() {
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.("(display-mode: standalone)")?.matches || navigatorWithStandalone.standalone === true;
}

function isIosDevice() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isMacSafari() {
  const userAgent = window.navigator.userAgent;
  return /macintosh/i.test(userAgent) && /safari/i.test(userAgent) && !/chrome|chromium|edg/i.test(userAgent);
}

// local-only metadata (order only; older payloads may still include color during migration)
type ClassMeta = {
  order: number;
  color?: string;
};
type MetaStore = Record<string, ClassMeta>;
type LegacyColorStore = Record<string, string>;

type DayKey = "Mon" | "Tue" | "Wed" | "Thu" | "Fri";
type SlotKind = "period" | "break" | "lunch";

type Slot = {
  id: string;
  kind: SlotKind;
  label: string;
  start: string;
  end: string;
};

type TimetableEntry = {
  classId: number | null;
  classLabel: string;
  room: string;
  supervisionRank: number | null;
  dutyNote: string;
};

type DaySchedule = {
  slots: Slot[];
  entries: Record<string, TimetableEntry>;
};

type TeacherProfile = {
  title: string;
  firstName: string;
  surname: string;
  schoolName: string;
  schoolAddress: string;
  rollNumber: string;
};

type StoredAdminState = {
  profile: TeacherProfile;
  schedule: Record<DayKey, DaySchedule>;
  updatedAt: string | null;
};

const TT_DAYS: DayKey[] = ["Mon", "Tue", "Wed", "Thu", "Fri"];

function fmtDayLong(d: DayKey) {
  if (d === "Mon") return "Monday";
  if (d === "Tue") return "Tuesday";
  if (d === "Wed") return "Wednesday";
  if (d === "Thu") return "Thursday";
  return "Friday";
}

function currentSchoolDay(): DayKey {
  const n = new Date().getDay(); // 0 Sun, 1 Mon ... 6 Sat
  if (n === 0 || n === 6) return "Mon"; // weekend → next Monday
  return TT_DAYS[n - 1] as DayKey;
}

function isLandscapeNow() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(orientation: landscape)").matches;
}

function loadTeacherTimetableLocal(): StoredAdminState | null {
  try {
    const raw =
      localStorage.getItem(teacherAdminStorageKeyForUser()) ??
      localStorage.getItem(teacherAdminLegacyKeyForUser());

    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredAdminState;
    if (!parsed?.profile || !parsed?.schedule) return null;

    return parsed;
  } catch {
    return null;
  }
}

function getEmailFromToken(): string | null {
  const t = localStorage.getItem("elume_token");
  if (!t) return null;
  try {
    const payload = JSON.parse(atob(t.split(".")[1]));
    // common JWT fields
    return payload?.email ?? payload?.sub ?? payload?.username ?? null;
  } catch {
    return null;
  }
}

function teacherAdminStorageKeyForUser() {
  const email = getEmailFromToken() ?? "anon";
  return `elume_teacher_admin_v3__${email}`;
}

function teacherAdminLegacyKeyForUser() {
  const email = getEmailFromToken() ?? "anon";
  return `elume_teacher_admin_v2__${email}`;
}

function teacherAdminKeyForUser() {
  return teacherAdminStorageKeyForUser();
}

function loadTeacherWelcome(): string {
  try {
    const raw =
      localStorage.getItem(teacherAdminStorageKeyForUser()) ??
      localStorage.getItem(teacherAdminLegacyKeyForUser());

    if (!raw) return "";

    const parsed = JSON.parse(raw);
    const p = parsed?.profile;
    if (!p) return "";

    const title = String(p.title ?? "").trim();
    const surname = String(p.surname ?? "").trim();

    if (!title || !surname) return "";
    return `Welcome ${title} ${surname}`;
  } catch {
    return "";
  }
}

function metaKeyForUser() {
  const email = getEmailFromToken() ?? "anon";
  return `elume_class_layout_v1__${email}`;
}

function firstClassPromptDismissKeyForUser() {
  const email = getEmailFromToken() ?? "anon";
  return `elume_first_class_prompt_dismissed_v1__${email}`;
}


function isKnownClassColour(value: string | null | undefined): value is string {
  return normaliseClassColourKey(value) !== null;
}

function loadMeta(): { meta: MetaStore; legacyColors: LegacyColorStore; hasLegacyOrder: boolean } {
  try {
    const raw = localStorage.getItem(metaKeyForUser());
    if (!raw) return { meta: {}, legacyColors: {}, hasLegacyOrder: false };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { meta: {}, legacyColors: {}, hasLegacyOrder: false };

    const meta: MetaStore = {};
    const legacyColors: LegacyColorStore = {};
    let hasLegacyOrder = false;

    Object.entries(parsed as Record<string, any>).forEach(([key, value]) => {
      if (!value || typeof value !== "object") return;

      if (typeof value.order === "number" && Number.isFinite(value.order)) {
        meta[key] = { order: Math.trunc(value.order) };
        hasLegacyOrder = true;
      }

      if (isKnownClassColour(value.color)) {
        legacyColors[key] = value.color;
      }
    });

    return { meta, legacyColors, hasLegacyOrder };
  } catch {
    return { meta: {}, legacyColors: {}, hasLegacyOrder: false };
  }
}

function normalizeClassOrderMeta(classes: ClassItem[], meta: MetaStore): { meta: MetaStore; changed: boolean } {
  const currentIds = classes.map((c) => String(c.id));
  const indexed = classes.map((cls, index) => {
    const key = String(cls.id);
    const existingOrder = meta[key]?.order;
    return {
      key,
      index,
      order: typeof existingOrder === "number" && Number.isFinite(existingOrder) ? existingOrder : null,
    };
  });

  const ordered = [
    ...indexed
      .filter((item) => item.order != null)
      .sort((a, b) => (a.order as number) - (b.order as number) || a.index - b.index || a.key.localeCompare(b.key)),
    ...indexed.filter((item) => item.order == null).sort((a, b) => a.index - b.index || a.key.localeCompare(b.key)),
  ];

  const nextMeta: MetaStore = {};
  let changed = Object.keys(meta).some((key) => !currentIds.includes(key));

  ordered.forEach((item, index) => {
    nextMeta[item.key] = { order: index };
    if (meta[item.key]?.order !== index) {
      changed = true;
    }
  });

  return { meta: changed ? nextMeta : meta, changed };
}

function resolveClassTileColour(cls: ClassItem, legacyColors: LegacyColorStore, meta?: MetaStore): ClassColourKey {
  const serverColour = normaliseClassColourKey(cls.color);
  if (serverColour) return serverColour;

  const legacyColour = legacyColors[String(cls.id)] ?? meta?.[String(cls.id)]?.color;
  return resolveClassColourKey(legacyColour, cls.id);
}


function textClassForBg(bgClass: string) {
  if (
    bgClass.includes("bg-yellow") ||
    bgClass.includes("bg-amber") ||
    bgClass.includes("bg-lime") ||
    bgClass.includes("bg-slate-100") ||
    bgClass.includes("bg-slate-200") ||
    bgClass.includes("bg-white")
  ) {
    return "text-slate-900";
  }
  return "text-white";
}

// -------------------- Create Class hierarchy types --------------------
type Stream =
  | "Junior Cycle"
  | "Senior Cycle"
  | "Transition Year (TY)"
  | "LCA"
  | "LCVP"
  | "SEN"
  | "Clubs";

type YearOption =
  | "1st Year"
  | "2nd Year"
  | "3rd Year"
  | "5th Year"
  | "6th Year"
  | "TY"
  | "LCA"
  | "LCVP"
  | "SEN"
  | "Club";

type LevelOption =
  | "Common Level"
  | "Higher Level"
  | "Ordinary Level"
  | "Vocational/Practical"
  | "Link Modules + Traditional Subjects"
  | "Resource"
  | "Learning Support"
  | "L2LP (Level 1 & 2)"
  | "Sports"
  | "Coding"
  | "Music"
  | "Debating";

function yearOptionsForStream(s: Stream): YearOption[] {
  switch (s) {
    case "Junior Cycle":
      return ["1st Year", "2nd Year", "3rd Year"];
    case "Senior Cycle":
      return ["5th Year", "6th Year"];
    case "Transition Year (TY)":
      return ["TY"];
    case "LCA":
      return ["LCA"];
    case "LCVP":
      return ["LCVP"];
    case "SEN":
      return ["SEN"];
    case "Clubs":
      return ["Club"];
    default:
      return ["1st Year"];
  }
}

function levelOptionsForStream(s: Stream, y: YearOption): LevelOption[] {
  if (s === "Junior Cycle") {
    if (y === "1st Year") return ["Common Level"];
    if (y === "2nd Year" || y === "3rd Year")
      return ["Higher Level", "Ordinary Level", "Common Level"];
    return ["Common Level"];
  }
  if (s === "Senior Cycle") return ["Higher Level", "Ordinary Level"];
  if (s === "Transition Year (TY)") return ["Common Level"];
  if (s === "LCA") return ["Vocational/Practical"];
  if (s === "LCVP") return ["Link Modules + Traditional Subjects"];
  if (s === "SEN") return ["Resource", "Learning Support", "L2LP (Level 1 & 2)"];
  if (s === "Clubs") return ["Sports", "Coding", "Music", "Debating"];
  return ["Common Level"];
}

const DEMO_DASHBOARD_PREVIEWS = [
  { name: "1st Year Maths", subject: "Mathematics", colour: "from-sky-500 to-cyan-600" },
  { name: "2nd Year Science", subject: "Science", colour: "from-violet-500 to-purple-600" },
  { name: "3rd Year Maths", subject: "Mathematics", colour: "from-indigo-500 to-blue-600" },
  { name: "TY Physics", subject: "Physics", colour: "from-fuchsia-500 to-pink-600" },
  { name: "5th Year Physics", subject: "Physics", colour: "from-amber-400 to-orange-500" },
] as const;

export function Dashboard({
  installPromptEvent,
  installedApp,
  onInstallPromptConsumed,
}: {
  installPromptEvent: BeforeInstallPromptEvent | null;
  installedApp: boolean;
  onInstallPromptConsumed: () => void;
}) {
  const demoMode = (() => {
    try { return Boolean(sessionStorage.getItem("elume_demo_token")); } catch { return false; }
  })();
  const { t, language } = useUiLanguage();
  const loadedMetaRef = useRef<{ meta: MetaStore; legacyColors: LegacyColorStore; hasLegacyOrder: boolean } | null>(null);
  if (!loadedMetaRef.current) {
    loadedMetaRef.current = loadMeta();
  }

  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serverClassLoadStatus, setServerClassLoadStatus] = useState<ServerClassLoadStatus>("loading");
  const [serverClassCount, setServerClassCount] = useState<number | null>(null);
  const [firstClassPromptDismissed, setFirstClassPromptDismissed] = useState(
    () => localStorage.getItem(firstClassPromptDismissKeyForUser()) === "1"
  );
  const [welcome, setWelcome] = useState<string>(() => loadTeacherWelcome());
  const [installDismissed, setInstallDismissed] = useState(() => localStorage.getItem(TEACHER_INSTALL_DISMISS_KEY) === "1");
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const runningStandalone = installedApp || isStandaloneApp();
  const manualInstallAvailable = isIosDevice() || isMacSafari();
  const installAvailable = !runningStandalone && (Boolean(installPromptEvent) || manualInstallAvailable);

  function dismissInstallPrompt() {
    try {
      localStorage.setItem(TEACHER_INSTALL_DISMISS_KEY, "1");
    } catch { }
    setInstallDismissed(true);
    setShowInstallInstructions(false);
  }

  function showInstallPromptAgain() {
    try {
      localStorage.removeItem(TEACHER_INSTALL_DISMISS_KEY);
    } catch { }
    setInstallDismissed(false);
  }

  async function requestNativeInstall() {
    if (!installPromptEvent) return;
    try {
      await installPromptEvent.prompt();
      const choice = await installPromptEvent.userChoice;
      if (choice.outcome === "accepted") dismissInstallPrompt();
    } catch { }
    onInstallPromptConsumed();
  }

  // Legacy browser metadata is used only to seed the server once after this release.
  const [meta] = useState<MetaStore>(() => loadedMetaRef.current?.meta ?? {});
  const legacyColourRef = useRef<LegacyColorStore>(loadedMetaRef.current?.legacyColors ?? {});
  const hasLegacyOrderRef = useRef(loadedMetaRef.current?.hasLegacyOrder ?? false);
  const orderMigrationAttemptedRef = useRef(false);
  const migratedColourIdsRef = useRef<Set<number>>(new Set());
  const dragIdRef = useRef<number | null>(null);
  const orderRequestVersionRef = useRef(0);

  // header clock
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // create modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [stream, setStream] = useState<Stream>("Junior Cycle");
  const [year, setYear] = useState<YearOption>("1st Year");
  const [level, setLevel] = useState<LevelOption>("Common Level");
  const [subject, setSubject] = useState("Maths");
  const [customClassMode, setCustomClassMode] = useState(false);
  const [customClassName, setCustomClassName] = useState("");
  const [customClassSubject, setCustomClassSubject] = useState("");
  const [pickedColour, setPickedColour] = useState<ClassColourKey>(DEFAULT_CLASS_COLOUR_KEY);
  const [creating, setCreating] = useState(false);

  const openCreate = useCallback(() => {
    setStream("Junior Cycle");
    setYear("1st Year");
    setLevel("Common Level");
    setSubject("Maths");
    setCustomClassMode(false);
    setCustomClassName("");
    setCustomClassSubject("");
    setPickedColour(DEFAULT_CLASS_COLOUR_KEY);
    setCreateOpen(true);
  }, []);
  const showDemoDashboardRestriction = useCallback(() => {
    window.dispatchEvent(new CustomEvent("elume-demo-restricted", { detail: { message: t("demo.previewLocked") } }));
  }, [t]);
  const openCreateFromDashboard = useCallback(() => {
    if (demoMode) {
      showDemoDashboardRestriction();
      return;
    }
    openCreate();
  }, [demoMode, openCreate, showDemoDashboardRestriction]);

  // edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [editColour, setEditColour] = useState<ClassColourKey>(DEFAULT_CLASS_COLOUR_KEY);
  const [savingEdit, setSavingEdit] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageClass, setManageClass] = useState<ClassItem | null>(null);
  const [manageBusy, setManageBusy] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);

  // mobile timetable quick-view
  const [ttOpen, setTtOpen] = useState(false);
  const [ttMode, setTtMode] = useState<"day" | "week">(
    () => (isLandscapeNow() ? "week" : "day")
  );
  const [ttDay, setTtDay] = useState<DayKey>(() => currentSchoolDay());
  const [ttState, setTtState] = useState<StoredAdminState | null>(() => loadTeacherTimetableLocal());
  const [ttLoading, setTtLoading] = useState(false);
  const [ttError, setTtError] = useState<string | null>(null);
  const [ttDesktopOpen, setTtDesktopOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setServerClassLoadStatus("loading");
    setServerClassCount(null);

    apiFetch("/classes")
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data)) throw new Error("Invalid classes response");
        const arr = data;
        setServerClassCount(arr.length);
        setServerClassLoadStatus("ready");

        setClasses((prev) => {
          // Prefer server truth; keep local extras only if server doesn't have them yet
          const byId = new Map<number, any>();

          arr.forEach((c: any) => byId.set(c.id, c));      // server wins
          prev.forEach((c: any) => {
            if (!byId.has(c.id)) byId.set(c.id, c);        // keep local extras
          });

          return Array.from(byId.values());
        });
      })

      .catch((e: any) => {
        if (cancelled) return;
        setError(userFacingError(e, "We couldn’t load your classes just yet. Give it another try."));
        setServerClassLoadStatus("error");
        setClasses([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handoff = consumeCreateClassHandoff(location.state);
    if (!handoff.shouldOpenCreate) return;

    openCreate();
    // Consume the one-time request so a refresh or Back navigation cannot reopen the modal.
    navigate(location.pathname, { replace: true, state: handoff.nextState });
  }, [location.pathname, location.state, navigate, openCreate]);

  useEffect(() => {
    const onStorage = () => setWelcome(loadTeacherWelcome());
    window.addEventListener("storage", onStorage);

    // also refresh when user comes back to the tab
    const onFocus = () => setWelcome(loadTeacherWelcome());
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (demoMode) return;
    if (
      classes.length === 0 ||
      !hasLegacyOrderRef.current ||
      orderMigrationAttemptedRef.current ||
      classes.some((cls) => cls.dashboard_order != null)
    ) {
      return;
    }

    const normalized = normalizeClassOrderMeta(classes, meta).meta;
    const classIds = Object.entries(normalized)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([id]) => Number(id));
    if (classIds.length !== classes.length || classIds.some((id) => !Number.isInteger(id) || id <= 0)) return;

    orderMigrationAttemptedRef.current = true;
    apiFetch("/classes/dashboard-order", {
      method: "PUT",
      body: JSON.stringify({ class_ids: classIds }),
    })
      .then((updated) => {
        if (Array.isArray(updated)) setClasses(updated as ClassItem[]);
      })
      .catch(() => {
        // Preserve the legacy local order; a later dashboard load can try again.
      });
  }, [classes, meta, demoMode]);

  useEffect(() => {
    if (demoMode) return;
    const pending = classes.filter((cls) => {
      if (normaliseClassColourKey(cls.color)) return false;
      const legacyColor = legacyColourRef.current[String(cls.id)];
      return isKnownClassColour(legacyColor) && !migratedColourIdsRef.current.has(cls.id);
    });

    if (pending.length === 0) return;

    let cancelled = false;

    (async () => {
      for (const cls of pending) {
        const legacyColor = legacyColourRef.current[String(cls.id)];
        const canonicalColour = normaliseClassColourKey(legacyColor);
        if (!canonicalColour) continue;

        migratedColourIdsRef.current.add(cls.id);

        try {
          const updated = (await apiFetch(`/classes/${cls.id}`, {
            method: "PUT",
            body: JSON.stringify({
              name: cls.name,
              subject: cls.subject,
              color: canonicalColour,
            }),
          })) as ClassItem;

          if (cancelled) return;

          setClasses((prev) =>
            prev.map((item) =>
              item.id === cls.id ? { ...item, ...updated, color: updated?.color ?? canonicalColour } : item
            )
          );
          delete legacyColourRef.current[String(cls.id)];
        } catch {
          // Keep the local fallback in place for this session.
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [classes, demoMode]);

  const sortedClasses = useMemo(() => {
    const copy = [...classes];
    const useLegacyOrder = classes.length > 0 && classes.every((cls) => cls.dashboard_order == null) && hasLegacyOrderRef.current;
    copy.sort((a, b) => {
      const ao = useLegacyOrder ? meta[String(a.id)]?.order ?? Number.MAX_SAFE_INTEGER : a.dashboard_order ?? Number.MAX_SAFE_INTEGER;
      const bo = useLegacyOrder ? meta[String(b.id)]?.order ?? Number.MAX_SAFE_INTEGER : b.dashboard_order ?? Number.MAX_SAFE_INTEGER;
      return ao - bo || a.id - b.id;
    });
    return copy;
  }, [classes, meta]);

  // Dynamic calendar output deliberately remains locale-formatted, not reviewer-editable UI text.
  const { day: headerDay, date: headerDate, time: headerTime } = formatDashboardDateCard(now, language);
  const plannerTeacherName = demoMode ? "Demo User" : (welcome ? welcome.replace(/^Welcome\s*/i, "") : "");
  const plannerGreeting = plannerTeacherName ? `${t("planner.welcome")}, ${plannerTeacherName}` : t("planner.welcome");

  async function loadTimetableReference(preferredMode: "day" | "week") {
    setTtDay(currentSchoolDay());
    setTtMode(preferredMode);
    setTtError(null);

    const local = loadTeacherTimetableLocal();
    if (local) setTtState(local);

    setTtLoading(true);
    try {
      const data = await apiFetch("/teacher-admin/state");
      const serverState = (data as any)?.state as StoredAdminState | null;
      if (serverState?.profile && serverState?.schedule) {
        setTtState(serverState);
        try {
          localStorage.setItem(teacherAdminStorageKeyForUser(), JSON.stringify(serverState));
        } catch { }
      } else if (!local) {
        setTtError("No timetable saved yet.");
      }
    } catch {
      if (!local) setTtError("Could not load timetable.");
    } finally {
      setTtLoading(false);
    }
  }

  async function openTimetableQuickView() {
    setTtOpen(true);
    await loadTimetableReference(isLandscapeNow() ? "week" : "day");
  }

  async function openDesktopTimetableQuickView() {
    setTtDesktopOpen(true);
    await loadTimetableReference("week");
  }

  function timetableCardClass(slot: Slot, hasEntry: boolean) {
    if (slot.kind === "break") return "border-amber-200 bg-amber-50 text-amber-900";
    if (slot.kind === "lunch") return "border-orange-200 bg-orange-50 text-orange-900";
    if (hasEntry) return "border-emerald-200 bg-emerald-50 text-slate-900";
    return "border-slate-200 bg-slate-50 text-slate-700";
  }

  function renderDayStack(day: DayKey) {
    if (!ttState?.schedule?.[day]) return null;

    const daySchedule = ttState.schedule[day];

    return (
      <div className="space-y-3">
        {daySchedule.slots.map((slot) => {
          const entry = daySchedule.entries?.[slot.id];
          const visual = timetableTileVisual(slot, entry);
          const hasClass = !!entry?.classLabel?.trim();
          const hasDuty = !!entry?.dutyNote?.trim();

          return (
            <div
              key={`${day}_${slot.id}`}
              className={`rounded-2xl border p-3 ${visual.tile}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-extrabold">{slot.label}</div>
                  <div className={`text-xs font-semibold ${visual.caption}`}>
                    {slot.start}–{slot.end}
                  </div>
                </div>

                <div className={`text-right text-xs font-semibold ${visual.caption}`}>
                  {fmtDayLong(day)}
                </div>
              </div>

              <div className="mt-2">
                {hasClass ? (
                  <>
                    <div className="text-base font-extrabold">{entry.classLabel}</div>
                    {entry.room && (
                      <div className={`mt-1 text-sm font-semibold ${visual.caption}`}>Room: {entry.room}</div>
                    )}
                  </>
                ) : hasDuty ? (
                  <div className="text-sm font-semibold">{entry.dutyNote}</div>
                ) : (
                  <div className="text-sm opacity-70">Free</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderWeekGrid() {
    if (!ttState?.schedule?.Mon) return null;

    const mondaySlots = ttState.schedule["Mon"].slots;

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[820px] rounded-2xl border border-slate-200 bg-white">
          <div className="grid grid-cols-6 border-b border-slate-200 bg-slate-50 text-xs font-bold text-slate-700">
            <div className="p-3">Time</div>
            {TT_DAYS.map((d) => (
              <div key={d} className={`p-3 ${ttDay === d ? "text-emerald-800" : ""}`}>
                {fmtDayLong(d)}
              </div>
            ))}
          </div>

          {mondaySlots.map((rowSlot) => (
            <div key={rowSlot.id} className="grid grid-cols-6 border-b border-slate-100 last:border-b-0">
              <div className="p-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-700">{rowSlot.label}</div>
                <div>{rowSlot.start}–{rowSlot.end}</div>
              </div>

              {TT_DAYS.map((day) => {
                const slot = ttState.schedule[day].slots.find((s) => s.id === rowSlot.id) ?? rowSlot;
                const entry = ttState.schedule[day].entries?.[rowSlot.id];
                const hasClass = !!entry?.classLabel?.trim();
                const hasDuty = !!entry?.dutyNote?.trim();

                return (
                  <div key={`${day}_${rowSlot.id}`} className="p-2">
                    <div className={`rounded-xl border p-2 min-h-[76px] ${timetableCardClass(slot, hasClass || hasDuty)}`}>
                      {hasClass ? (
                        <>
                          <div className="text-xs font-extrabold leading-tight">{entry.classLabel}</div>
                          {entry.room && <div className="mt-1 text-[11px]">Room: {entry.room}</div>}
                        </>
                      ) : hasDuty ? (
                        <div className="text-[11px] font-semibold leading-tight">{entry.dutyNote}</div>
                      ) : (
                        <div className="text-[11px] opacity-70">Free</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function timetableTileVisual(slot: Slot, entry?: TimetableEntry) {
    const hasClass = !!entry?.classLabel?.trim();
    const hasDuty = !!entry?.dutyNote?.trim();

    if (hasClass) {
      const cls = entry?.classId != null ? classes.find((item) => item.id === entry.classId) : undefined;
      const bg = classColourBackgroundClass(
        cls ? resolveClassTileColour(cls, legacyColourRef.current, meta) : DEFAULT_CLASS_COLOUR_KEY
      );
      return {
        tile: `border-black/70 ${bg} ${textClassForBg(bg)} shadow-[0_4px_0_rgba(15,23,42,0.18)]`,
        caption: textClassForBg(bg) === "text-white" ? "text-white/85" : "text-slate-700",
      };
    }

    if (slot.kind === "break") {
      return {
        tile: "border-amber-200 bg-amber-50 text-amber-900",
        caption: "text-amber-700",
      };
    }
    if (slot.kind === "lunch") {
      return {
        tile: "border-orange-200 bg-orange-50 text-orange-900",
        caption: "text-orange-700",
      };
    }
    if (hasDuty) {
      return {
        tile: "border-cyan-200 bg-cyan-50 text-cyan-900",
        caption: "text-cyan-700",
      };
    }
    return {
      tile: "border-slate-200 bg-slate-50 text-slate-700",
      caption: "text-slate-500",
    };
  }

  function renderDesktopDayStackBranded(day: DayKey) {
    if (!ttState?.schedule?.[day]) return null;

    const daySchedule = ttState.schedule[day];
    const isToday = currentSchoolDay() === day;

    return (
      <div className="space-y-3">
        {daySchedule.slots.map((slot) => {
          const entry = daySchedule.entries?.[slot.id];
          const visual = timetableTileVisual(slot, entry);
          const hasClass = !!entry?.classLabel?.trim();
          const hasDuty = !!entry?.dutyNote?.trim();

          return (
            <div
              key={`${day}_${slot.id}`}
              className={`rounded-[24px] border p-4 ${visual.tile} ${isToday ? "ring-2 ring-emerald-200/80" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-black uppercase tracking-[0.16em] opacity-80">{slot.label}</div>
                  <div className={`mt-1 text-xs font-semibold ${visual.caption}`}>
                    {slot.start}–{slot.end}
                  </div>
                </div>
                {isToday && (
                  <div className="rounded-full border border-white/40 bg-white/20 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]">
                    Today
                  </div>
                )}
              </div>

              <div className="mt-3">
                {hasClass ? (
                  <>
                    <div className="text-lg font-black leading-tight">{entry?.classLabel}</div>
                    {entry?.room && <div className={`mt-1 text-sm font-semibold ${visual.caption}`}>Room: {entry.room}</div>}
                  </>
                ) : hasDuty ? (
                  <div className="text-sm font-semibold leading-tight">{entry?.dutyNote}</div>
                ) : (
                  <div className="text-sm font-semibold opacity-70">Free</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function isCompactReferenceSlot(slot: Slot) {
    if (slot.kind === "break" || slot.kind === "lunch") return true;

    const label = String(slot.label ?? "").toLowerCase();
    return label.includes("supervision");
  }

  function desktopTimetableRowClasses(slot: Slot) {
    if (isCompactReferenceSlot(slot)) {
      return {
        cellPad: "p-1.5",
        tile: "min-h-[42px] rounded-[14px] px-3 py-2",
        title: "text-[11px] font-semibold leading-tight",
        room: "hidden",
        free: "text-[11px] font-semibold opacity-70",
        duty: "text-[11px] font-semibold leading-tight",
        timePad: "p-2",
        timeTitle: "font-black uppercase tracking-[0.1em] text-slate-700",
        timeText: "mt-0.5 font-semibold",
      };
    }

    return {
      cellPad: "p-2",
      tile: "min-h-[74px] rounded-[18px] p-2.5",
      title: "text-[13px] font-black leading-tight",
      room: "mt-1 text-[11px] font-semibold",
      free: "text-[11px] font-semibold opacity-70",
      duty: "text-[11px] font-semibold leading-tight",
      timePad: "p-3",
      timeTitle: "font-black uppercase tracking-[0.12em] text-slate-700",
      timeText: "mt-0.5 font-semibold",
    };
  }

  function renderDesktopWeekGridBranded() {
    if (!ttState?.schedule?.Mon) return null;

    const mondaySlots = ttState.schedule["Mon"].slots;
    const today = currentSchoolDay();

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[980px] rounded-[24px] border border-white/70 bg-white/80 shadow-inner backdrop-blur">
          <div className="grid grid-cols-6 border-b border-slate-200/80 bg-gradient-to-r from-slate-50 via-white to-emerald-50 text-[11px] font-black uppercase tracking-[0.14em] text-slate-600">
            <div className="p-3">Time</div>
            {TT_DAYS.map((d) => (
              <div
                key={d}
                className={`p-3 ${today === d ? "bg-emerald-50/80 text-emerald-800" : ""}`}
              >
                <div>{fmtDayLong(d)}</div>
                {today === d && (
                  <div className="mt-1 inline-flex rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[9px] font-black text-emerald-700">
                    Today
                  </div>
                )}
              </div>
            ))}
          </div>

          {mondaySlots.map((rowSlot) => {
            const rowUi = desktopTimetableRowClasses(rowSlot);

            return (
              <div
                key={rowSlot.id}
                className="grid grid-cols-6 border-b border-slate-100/90 last:border-b-0"
              >
                <div className={`${rowUi.timePad} text-[11px] text-slate-600`}>
                  <div className={rowUi.timeTitle}>{rowSlot.label}</div>
                  <div className={rowUi.timeText}>
                    {rowSlot.start}–{rowSlot.end}
                  </div>
                </div>

                {TT_DAYS.map((day) => {
                  const slot =
                    ttState.schedule[day].slots.find((s) => s.id === rowSlot.id) ?? rowSlot;
                  const entry = ttState.schedule[day].entries?.[rowSlot.id];
                  const visual = timetableTileVisual(slot, entry);
                  const hasClass = !!entry?.classLabel?.trim();
                  const hasDuty = !!entry?.dutyNote?.trim();

                  return (
                    <div
                      key={`${day}_${rowSlot.id}`}
                      className={`${rowUi.cellPad} ${today === day ? "bg-emerald-50/30" : ""}`}
                    >
                      <div className={`border ${rowUi.tile} ${visual.tile}`}>
                        {hasClass ? (
                          <>
                            <div className={rowUi.title}>{entry?.classLabel}</div>
                            {!isCompactReferenceSlot(slot) && entry?.room && (
                              <div className={`${rowUi.room} ${visual.caption}`}>
                                Room: {entry.room}
                              </div>
                            )}
                          </>
                        ) : hasDuty ? (
                          <div className={rowUi.duty}>{entry?.dutyNote}</div>
                        ) : (
                          <div className={rowUi.free}>Free</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Design tokens
  const card =
    "rounded-3xl border-2 border-slate-200 bg-white shadow-[0_2px_0_rgba(15,23,42,0.06)]";
  const btn =
    "rounded-2xl border-2 border-slate-300 bg-white px-6 py-3 text-base font-semibold shadow-sm hover:bg-slate-50 active:translate-y-[1px]";
  const btnGlow =
    "relative inline-flex items-center gap-3 rounded-2xl border-2 border-emerald-600 " +
    "bg-gradient-to-r from-sky-500 to-emerald-500 " +
    "px-7 py-3 text-base font-extrabold text-white " +
    "shadow-[0_0_18px_rgba(120,120,120,0.35)] " +
    "ring-4 ring-slate-300/60 " +
    "hover:shadow-[0_0_40px_rgba(120,120,120,0.6)] hover:ring-slate-400 " +
    "hover:-translate-y-[2px] hover:scale-[1.03] active:scale-[0.98] " +
    "transition-all duration-200 overflow-hidden " +
    "after:absolute after:top-0 after:left-[-60%] after:h-full after:w-[60%] " +
    "after:bg-gradient-to-r after:from-transparent after:via-white/40 after:to-transparent " +
    "after:rotate-12 hover:after:left-[120%] after:transition-all after:duration-700";
  const btnPrimary =
    "rounded-2xl border-2 border-emerald-700 bg-emerald-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-700 active:translate-y-[1px] disabled:opacity-50 ";

  const pill =
    "rounded-full border-2 border-slate-200 bg-slate-50 px-4 py-2 text-sm hover:bg-slate-100 active:translate-y-[1px]";

  const headerBtn =
    "w-32 text-center rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm backdrop-blur transition-all hover:bg-slate-100 hover:shadow-md hover:-translate-y-[1px] active:translate-y-[0px] w-32 text-center";

  const headerBtnPrimary =
    "w-32 text-center rounded-2xl border border-emerald-600 bg-emerald-600/90 px-4 py-2 text-sm font-semibold text-white shadow-sm backdrop-blur transition-all hover:bg-emerald-700 hover:border-emerald-700 hover:shadow-md hover:-translate-y-[1px] active:translate-y-[0px] w-32 text-center";

  function swapOrder(dragId: number, overId: number) {
    if (dragId === overId) return;

    const nextIds = sortedClasses.map((cls) => cls.id);
    const dragIndex = nextIds.indexOf(dragId);
    const overIndex = nextIds.indexOf(overId);
    if (dragIndex < 0 || overIndex < 0) return;

    [nextIds[dragIndex], nextIds[overIndex]] = [nextIds[overIndex], nextIds[dragIndex]];
    const previousClasses = classes;
    const orderById = new Map(nextIds.map((id, order) => [id, order]));
    const requestVersion = ++orderRequestVersionRef.current;

    setClasses((previous) => previous.map((cls) => ({ ...cls, dashboard_order: orderById.get(cls.id) ?? cls.dashboard_order })));
    apiFetch("/classes/dashboard-order", {
      method: "PUT",
      body: JSON.stringify({ class_ids: nextIds }),
    })
      .then((updated) => {
        if (requestVersion === orderRequestVersionRef.current && Array.isArray(updated)) {
          setClasses(updated as ClassItem[]);
        }
      })
      .catch(() => {
        if (requestVersion === orderRequestVersionRef.current) {
          setClasses(previousClasses);
          setError(CLASS_ORDER_SAVE_ERROR);
        }
      });
  }

  function suggestedName() {
    const subj = subject.trim() || "Subject";

    if (stream === "Transition Year (TY)") return `TY ${subj}`;
    if (stream === "LCA") return `LCA ${subj}`;
    if (stream === "LCVP") return `LCVP ${subj}`;
    if (stream === "SEN") return `SEN ${level} ${subj}`.replace(/\s+/g, " ").trim();
    if (stream === "Clubs") return `Club ${level}`.replace(/\s+/g, " ").trim();

    const lvlSuffix =
      level === "Higher Level"
        ? " (Higher)"
        : level === "Ordinary Level"
          ? " (Ordinary)"
          : "";

    return `${year} ${subj}${lvlSuffix}`.trim();
  }

  async function createClass() {
    const name = customClassMode ? customClassName.trim() : year.trim();
    const subj = customClassMode ? customClassSubject.trim() : subject.trim() || "Subject";
    if (!name) return;
    if (customClassMode && !subj) return;
    const shouldSeedLegacyOrder =
      hasLegacyOrderRef.current &&
      classes.length > 0 &&
      classes.every((cls) => cls.dashboard_order == null);

    setCreating(true);
    setError(null);

    try {
      const payload = customClassMode
        ? { name, subject: subj, color: pickedColour }
        : { name, subject: subj, stream, color: pickedColour };
      const created = (await apiFetch("/classes", {
        method: "POST",
        body: JSON.stringify(payload),
      })) as ClassItem;

      console.log("CREATE /classes response:", created);

      const createdFixed: ClassItem = {
        ...created,
        name: (created as any)?.name ?? name,
        subject: (created as any)?.subject ?? subj,
        color: normaliseClassColourKey((created as any)?.color) ?? pickedColour,
      };

      if (!createdFixed?.id) throw new Error("Create returned no id");

      setClasses((prev) => [createdFixed, ...prev]);
      // The create request succeeded, so this account is no longer empty even if the refresh below fails.
      setServerClassCount(1);
      setServerClassLoadStatus("ready");

      const fresh = await apiFetch("/classes");
      if (!Array.isArray(fresh)) throw new Error("Invalid classes response");
      const arr = fresh;
      setServerClassCount(arr.length);
      setServerClassLoadStatus("ready");
      if (shouldSeedLegacyOrder) {
        const legacyOrder = normalizeClassOrderMeta(
          arr.filter((cls) => cls.id !== createdFixed.id),
          meta
        ).meta;
        const classIds = [
          createdFixed.id,
          ...Object.entries(legacyOrder)
            .sort(([, a], [, b]) => a.order - b.order)
            .map(([id]) => Number(id)),
        ];
        try {
          const reordered = await apiFetch("/classes/dashboard-order", {
            method: "PUT",
            body: JSON.stringify({ class_ids: classIds }),
          });
          setClasses(Array.isArray(reordered) ? reordered : arr);
        } catch {
          // The class itself was created successfully; keep the server list and retry legacy order next visit.
          setClasses(arr);
        }
      } else {
        setClasses(arr);
      }


      setCreateOpen(false);
    } catch (e: any) {
      setError(userFacingError(e, "We couldn’t create that class just now. Please try again."));
    } finally {
      setCreating(false);
    }
  }

  function dismissFirstClassPrompt() {
    try {
      localStorage.setItem(firstClassPromptDismissKeyForUser(), "1");
    } catch {
      // Keep the in-memory dismissal even when storage is unavailable.
    }
    setFirstClassPromptDismissed(true);
  }

  const showFirstClassPrompt = shouldShowFirstClassPrompt(
    serverClassLoadStatus,
    serverClassCount,
    firstClassPromptDismissed
  );

  function openEdit(c: ClassItem) {
    setEditingId(c.id);
    setEditName(c.name || "");
    setEditSubject(c.subject || "");
    setEditColour(resolveClassTileColour(c, legacyColourRef.current, meta));
    setEditOpen(true);
  }

  function openManageClass(c: ClassItem) {
    setManageClass(c);
    setManageError(null);
    setManageOpen(true);
  }

  async function archiveSelectedClass() {
    if (!manageClass) return;
    setManageBusy(true);
    setManageError(null);
    try {
      await apiFetch(`/classes/${manageClass.id}/archive`, { method: "POST" });
      setClasses((prev) => prev.filter((cls) => cls.id !== manageClass.id));
      setManageOpen(false);
      setManageClass(null);
    } catch (e: any) {
      setManageError(userFacingError(e, "We couldn’t archive that class just now. Please try again."));
    } finally {
      setManageBusy(false);
    }
  }

  async function deleteSelectedClassPermanently() {
    if (!manageClass) return;
    setManageBusy(true);
    setManageError(null);
    try {
      await apiFetch(`/classes/${manageClass.id}`, { method: "DELETE" });
      setClasses((prev) => prev.filter((cls) => cls.id !== manageClass.id));
      setManageOpen(false);
      setManageClass(null);
    } catch (e: any) {
      setManageError(userFacingError(e, "We couldn’t delete that class just now. Please try again."));
    } finally {
      setManageBusy(false);
    }
  }

  async function saveEdit() {
    if (editingId == null) return;

    const name = editName.trim();
    const subj = editSubject.trim();

    if (!name) {
      setError("Class name cannot be empty.");
      return;
    }

    setSavingEdit(true);
    setError(null);

    try {
      // 1) Save name/subject to backend
      const updated = (await apiFetch(`/classes/${editingId}`, {
        method: "PUT",
        body: JSON.stringify({ name, subject: subj || "Subject", color: editColour }),
      })) as ClassItem;


      // update local list
      setClasses((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      delete legacyColourRef.current[String(editingId)];

      setEditOpen(false);
      setEditingId(null);
    } catch (e: any) {
      setError(userFacingError(e, "We couldn’t save those class changes just now. Please try again."));
    } finally {
      setSavingEdit(false);
    }
  }

  return (
    <div className="min-h-screen bg-emerald-100">
      {/* Header bar */}
      <header className="border-b-2 border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center px-4 py-6">
          {/* Left brand */}
          <div className="flex items-center gap-5">
            <img src={ELogo2} alt="ELume" className="h-28 w-auto object-contain" />
            <div className="leading-tight">
              <div className="hidden md:block text-5xl font-black tracking-tight leading-none">
                <div
                  className="hidden md:block text-5xl font-extrabold tracking-tight text-slate-700"
                  style={{ textShadow: "0 3px 8px rgba(0,0,0,0.25)" }}
                >
                  Elume
                </div>
              </div>
              <div className="hidden md:block text-base font-semibold text-slate-500">
                Learn, Grow, Succeed
              </div>
            </div>
          </div>

          {/* Right controls */}
          <div className="ml-auto flex items-center gap-3 md:gap-4">
            {/* ✅ Mobile-only: Admin + Calendar beside logo */}
            <div className="flex items-center gap-2 md:hidden">
              <button
                className="rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm backdrop-blur transition-all hover:bg-slate-100"
                type="button"
                onClick={openTimetableQuickView}
              >
                <UiText translationKey="dashboard.timetable" />
              </button>

              <button
                className="rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm backdrop-blur transition-all hover:bg-slate-100"
                type="button"
                onClick={() => navigate("/admin")}
              >
                <UiText translationKey="nav.admin" />
              </button>

              <button
                className="rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm backdrop-blur transition-all hover:bg-slate-100"
                type="button"
                onClick={() => navigate("/calendar")}
              >
                <UiText translationKey="nav.calendar" />
              </button>
            </div>

            {/* Desktop-only: Create Resources (primary) */}
            <button
              className={`${dashboardCreateResourcesVisibilityClass()} ${btnGlow}`}
              type="button"
              onClick={() => navigate("/create-resources")}
              title="AI-powered lesson planning and resource creation"
            >
              <span className="relative flex items-center justify-center">
                {/* pulsing AI halo */}
                <span className="absolute inline-flex h-6 w-6 rounded-full bg-pink-400 opacity-60 animate-[ping_2.2s_ease-out_infinite]"></span>

                {/* brain icon */}
                <span className="relative text-lg leading-none drop-shadow-[0_0_6px_rgba(255,255,255,0.9)]">
                  🧠
                </span>
              </span>

              <span className="drop-shadow-[0_2px_2px_rgba(0,0,0,0.55)]"><UiText translationKey="dashboard.createResources" /></span>

              <span className="ml-1 rounded-full bg-white/20 px-2 py-[2px] text-[10px] font-bold tracking-wide text-white border border-white/40">
                AI
              </span>
            </button>

            {/* Admin + Calendar stacked to the right (desktop) */}
            <div className="hidden md:flex flex-col gap-2 items-end">
              <button className={headerBtn} type="button" onClick={() => navigate("/admin")}>
                <UiText translationKey="nav.admin" />
              </button>

              <button className={headerBtn} type="button" onClick={() => navigate("/calendar")}>
                <UiText translationKey="nav.calendar" />
              </button>
            </div>

            {/* Date card */}
            <div className="hidden md:block rounded-3xl border-2 border-slate-200 bg-slate-50 px-6 py-4 text-right shadow-sm">
              <div className="text-sm font-semibold text-slate-600">{headerDay}</div>
              <div className="text-lg font-extrabold text-slate-800">{headerDate}</div>
              <div className="text-sm font-semibold text-slate-600">{headerTime}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-7xl px-4 py-6">
        {error && (
          <InlineNotice
            variant="error"
            title="That didn’t quite work"
            message={error}
            className="mb-4"
          />
        )}

        {showFirstClassPrompt && (
          <section
            className="mb-5 overflow-hidden rounded-[28px] border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50 p-5 shadow-[0_14px_34px_rgba(16,185,129,0.12)]"
            aria-labelledby="first-class-welcome"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl">
                <h2 id="first-class-welcome" className="text-2xl font-extrabold tracking-tight text-slate-900">
                  Welcome to Elume
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-700">
                  Start by adding a class. You can add the rest whenever you’re ready, then arrange your timetable in Teacher Admin.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button type="button" className="rounded-2xl border-2 border-emerald-700 bg-emerald-600 px-5 py-2.5 text-sm font-extrabold text-white shadow-sm hover:bg-emerald-700" onClick={openCreateFromDashboard}>
                  Create my first class
                </button>
                <button type="button" className="rounded-2xl border-2 border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50" onClick={dismissFirstClassPrompt}>
                  Not now
                </button>
              </div>
            </div>
          </section>
        )}

        <div className="mb-5 w-full">
          {/* Mobile layout */}
          <div className="flex items-stretch gap-3 md:hidden">
            <button
              type="button"
              onClick={openCreateFromDashboard}
              className="shrink-0 rounded-2xl border-2 border-emerald-700 bg-emerald-600 px-5 py-2.5 text-xl font-extrabold text-white shadow-md hover:bg-emerald-700 active:translate-y-[1px]"
              style={{ textShadow: "0 2px 4px rgba(0,0,0,0.35)" }}
            >
              + {t("dashboard.createClass")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/planner")}
              title="Open ELume Planner"
              className="group min-w-0 flex-1 flex items-center gap-3 rounded-3xl border-2 border-emerald-200 bg-gradient-to-r from-white via-emerald-50 to-sky-50 px-4 py-2.5 shadow-[0_4px_14px_rgba(16,185,129,0.10)] hover:border-emerald-300 hover:from-emerald-50 hover:to-sky-100 active:translate-y-[1px] transition-all"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-white shadow-sm">
                <img
                  src={PlannerLogo}
                  alt="Planner"
                  className="h-8 w-8 object-contain"
                />
              </div>

              <div className="min-w-0 text-left leading-tight">
                <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-emerald-700/80">
                  ELume Planner
                </div>

                <div className="truncate text-lg font-extrabold tracking-tight text-slate-800">
                  {plannerGreeting}
                </div>

                <div className="text-[11px] text-slate-600">
                  {t("planner.tasksWeekView")}
                  <span className="ml-1 text-emerald-600 opacity-70 group-hover:opacity-100">→</span>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-3 text-base font-semibold text-slate-700 md:hidden">
            <UiText translationKey="dashboard.dragToArrange" />
          </div>

          {/* Desktop layout */}
          <div className="hidden w-full md:flex md:items-center md:gap-4">
            <button
              type="button"
              onClick={openCreateFromDashboard}
              className="rounded-2xl border-2 border-emerald-700 bg-emerald-600 px-6 py-2.5 text-xl font-extrabold text-white shadow-md hover:bg-emerald-700 active:translate-y-[1px]"
              style={{ textShadow: "0 2px 4px rgba(0,0,0,0.35)" }}
            >
              + {t("dashboard.createClass")}
            </button>

            <div className="text-base font-semibold text-slate-700">
              <UiText translationKey="dashboard.dragToArrange" />
            </div>

            <div className="ml-auto flex min-w-0 items-center gap-4">
              <button
                type="button"
                onClick={openDesktopTimetableQuickView}
                className="rounded-3xl border border-emerald-200 bg-gradient-to-r from-white via-emerald-50 to-cyan-50 px-5 py-3 text-sm font-semibold text-slate-800 shadow-[0_4px_14px_rgba(16,185,129,0.10)] transition-all hover:border-emerald-300 hover:shadow-md hover:-translate-y-[1px]"
              >
                {t("dashboard.viewTimetable")}
              </button>

              <button
                type="button"
                onClick={() => navigate("/planner")}
                title="Open ELume Planner"
                className="group flex min-w-0 w-full max-w-[420px] items-center gap-3 rounded-3xl border-2 border-emerald-200 bg-gradient-to-r from-white via-emerald-50 to-sky-50 px-4 py-2.5 shadow-[0_4px_14px_rgba(16,185,129,0.10)] hover:border-emerald-300 hover:from-emerald-50 hover:to-sky-100 active:translate-y-[1px] transition-all"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-white shadow-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgb(16 185 129)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-6 w-6"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="3" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <line x1="8" y1="14" x2="12" y2="14" />
                    <line x1="8" y1="18" x2="16" y2="18" />
                  </svg>
                </div>
                <div className="min-w-0 text-left leading-tight">
                  <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-emerald-700/80">
                    ELume Planner
                  </div>

                  <div className="truncate text-lg font-extrabold tracking-tight text-slate-800">
                    {plannerGreeting}
                  </div>

                  <div className="text-[11px] text-slate-600">
                    {t("planner.tasksWeekView")}
                    <span className="ml-1 text-emerald-600 opacity-70 group-hover:opacity-100">→</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {installAvailable && !installDismissed ? (
          <div className="mb-5 rounded-3xl border border-emerald-200 bg-[linear-gradient(135deg,rgba(236,253,245,0.95),rgba(255,255,255,0.98),rgba(236,254,255,0.92))] p-4 shadow-[0_10px_28px_rgba(16,185,129,0.12)]">
            <div className="flex flex-wrap items-center gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/80 bg-white shadow-sm">
                <img src={ELogo2} alt="Elume" className="h-8 w-8 object-contain" />
              </div>
              <div className="min-w-[220px] flex-1">
                <div className="text-base font-black tracking-tight text-slate-900">{t("dashboard.install")}</div>
                <div className="mt-1 text-sm leading-5 text-slate-600">Keep Elume one tap away during lessons. Install it on this device and open it just like an app.</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={installPromptEvent ? () => void requestNativeInstall() : () => setShowInstallInstructions(true)}
                  className="min-h-11 rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-4 py-2.5 text-sm font-black text-white shadow-md transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-200"
                >
                  {t("dashboard.install")}
                </button>
                <button
                  type="button"
                  onClick={dismissInstallPrompt}
                  className="min-h-11 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-200"
                >
                  {t("dashboard.maybeLater")}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {installAvailable && installDismissed ? (
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={showInstallPromptAgain}
              className="rounded-xl px-3 py-2 text-sm font-bold text-emerald-800 underline decoration-emerald-300 underline-offset-4 transition hover:text-emerald-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-200"
            >
              {t("dashboard.install")}
            </button>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {loading && (
            <div className={`${card} p-4 text-sm text-slate-600 md:col-span-4`}>
              {t("dashboard.loadingClasses")}
            </div>
          )}

          {serverClassLoadStatus === "ready" && serverClassCount === 0 && sortedClasses.length === 0 && (
            <div className={`${card} p-4 text-sm text-slate-600 md:col-span-4`}>
              {t("dashboard.noClasses")}
            </div>
          )}

          {!loading &&
            sortedClasses.map((c) => {
              const colourKey = resolveClassTileColour(c, legacyColourRef.current, meta);
              const bg = classColourBackgroundClass(colourKey);
              const txt = textClassForBg(bg);

              if (demoMode && c.name === "Demo Class") {
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/class/${c.id}`)}
                    className={`group relative h-48 w-full overflow-visible rounded-3xl border-[5px] border-violet-700 ${bg} ${txt} shadow-[0_4px_0_rgba(15,23,42,0.16)] ring-4 ring-emerald-300/80 transition-all duration-200 hover:-translate-y-[2px] hover:shadow-[0_12px_26px_rgba(0,0,0,0.22)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300 active:translate-y-[1px] active:scale-[0.98]`}
                    title={t("dashboard.openClass")}
                  >
                    <span className="absolute -top-3 left-4 rounded-full bg-violet-800 px-3 py-1 text-[10px] font-black tracking-[0.12em] text-white shadow-md">START HERE</span>
                    <span className="flex h-full flex-col items-center justify-center p-4 text-center">
                      <span className="text-3xl font-extrabold leading-tight tracking-tight" style={{ textShadow: "0 3px 6px rgba(0,0,0,0.45)" }}>{c.name}</span>
                      <span className="mt-1 text-lg font-semibold tracking-wide text-white/95" style={{ textShadow: "0 2px 5px rgba(0,0,0,0.4)" }}>{c.subject}</span>
                      <span className="mt-3 text-sm font-black text-white">Click here to try it out</span>
                      <span className="mt-1 text-xs font-semibold text-white/90">Explore your ready-made classroom</span>
                      <span className="mt-3 rounded-full bg-white/20 px-4 py-1.5 text-xs font-black">Open Demo Class</span>
                    </span>
                  </button>
                );
              }

              return (
                <div
                  key={c.id}
                  draggable={!demoMode}
                  onDragStart={() => { if (!demoMode) dragIdRef.current = c.id; }}
                  onDragOver={(e) => { if (!demoMode) e.preventDefault(); }}
                  onDrop={() => {
                    if (demoMode) return;
                    const dragId = dragIdRef.current;
                    if (dragId != null) swapOrder(dragId, c.id);
                    dragIdRef.current = null;
                  }}
                  onClick={() => navigate(`/class/${c.id}`)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(`/class/${c.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className={`group relative h-44 w-full rounded-3xl border-[5px] border-black ${bg} ${txt}
                    shadow-[0_4px_0_rgba(15,23,42,0.16)]
                    transition-all duration-200
                    hover:-translate-y-[2px] hover:shadow-[0_12px_26px_rgba(0,0,0,0.22)]
                    hover:ring-4 hover:ring-white/60
                    active:translate-y-[1px] active:scale-[0.98]
                    flex items-center justify-center p-4`}
                  title={t("dashboard.openClass")}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(c);
                    }}
                    className="absolute bottom-2 left-2 grid h-5 w-5 place-items-center rounded-lg border border-white/70 bg-white/90 text-[10px] shadow-sm opacity-80 transition hover:opacity-100 hover:bg-white"
                    title={t("dashboard.editClass")}
                    aria-label={`Edit ${c.name}`}
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openManageClass(c);
                    }}
                    className="absolute bottom-2 right-2 rounded-lg border border-white/70 bg-white/90 px-2 py-1 text-[10px] font-semibold text-slate-700 shadow-sm opacity-80 transition hover:opacity-100 hover:bg-white"
                    title={t("dashboard.manageClass")}
                    aria-label={`${t("dashboard.manageClass")}: ${c.name}`}
                  >
                    {t("dashboard.manageClass")}
                  </button>

                  <div className="text-center">
                    <div
                      className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight"
                      style={{ textShadow: "0 3px 6px rgba(0,0,0,0.45)" }}
                    >
                      {c.name}
                    </div>

                    <div
                      className="mt-1 text-xl md:text-2xl font-semibold tracking-wide text-white/95"
                      style={{ textShadow: "0 2px 5px rgba(0,0,0,0.4)" }}
                    >
                      {c.subject}
                    </div>
                    <div className="mt-3 inline-flex items-center justify-center rounded-full bg-white/20 px-4 py-1.5 text-xs font-semibold opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      {t("dashboard.openClass")} • {t("dashboard.dragToArrange")}
                    </div>
                  </div>
                </div>
              );
            })}
          {!loading && demoMode && DEMO_DASHBOARD_PREVIEWS.map((preview) => (
            <button
              key={preview.name}
              type="button"
              onClick={showDemoDashboardRestriction}
              className={`relative h-44 w-full rounded-3xl bg-gradient-to-br ${preview.colour} p-4 text-left text-white shadow-[0_4px_0_rgba(15,23,42,0.16)] transition hover:-translate-y-[2px] hover:shadow-[0_12px_26px_rgba(0,0,0,0.22)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300`}
              aria-label={`${preview.name}. ${t("demo.previewLocked")}`}
            >
              <span className="rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide">Preview class</span>
              <span className="mt-5 block text-2xl font-extrabold tracking-tight" style={{ textShadow: "0 2px 5px rgba(0,0,0,0.35)" }}>{preview.name}</span>
              <span className="mt-1 block text-sm font-semibold text-white/90">{preview.subject}</span>
              <span className="mt-4 inline-flex rounded-full bg-white/20 px-3 py-1.5 text-xs font-black">Open Demo Class</span>
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate("/archived-classes")}
            className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:shadow-md"
          >
            {t("dashboard.archivedClasses")}
          </button>
        </div>
      </main>

      {showInstallInstructions && !runningStandalone ? (
        <div className="fixed inset-0 z-[70] grid place-items-end bg-slate-950/40 p-3 sm:place-items-center" role="dialog" aria-modal="true" aria-labelledby="install-elume-title">
          <div className="w-full max-w-md rounded-[28px] border border-white/80 bg-white p-5 shadow-2xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-black uppercase tracking-[0.14em] text-emerald-700">Install Elume</div>
                <h2 id="install-elume-title" className="mt-1 text-2xl font-black tracking-tight text-slate-900">
                  {isIosDevice() ? "Install Elume on iPhone" : "Install Elume on your Mac"}
                </h2>
              </div>
              <button type="button" onClick={() => setShowInstallInstructions(false)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 shadow-sm hover:bg-slate-50" aria-label="Close installation instructions">
                Close
              </button>
            </div>
            {isIosDevice() ? (
              <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-6 text-slate-700">
                <li>Open Elume in Safari and tap the Share button.</li>
                <li>Choose <span className="font-black text-slate-900">Add to Home Screen</span>.</li>
                <li>Enable or choose <span className="font-black text-slate-900">Open as Web App</span> if that option is shown.</li>
                <li>Tap <span className="font-black text-slate-900">Add</span>.</li>
              </ol>
            ) : (
              <ol className="mt-5 list-decimal space-y-3 pl-5 text-sm leading-6 text-slate-700">
                <li>Open Elume in Safari.</li>
                <li>Choose <span className="font-black text-slate-900">File &gt; Add to Dock</span> or <span className="font-black text-slate-900">Share &gt; Add to Dock</span> if shown.</li>
                <li>Confirm to add Elume to your Dock.</li>
              </ol>
            )}
            <p className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">Elume will appear on your device and open like an app.</p>
          </div>
        </div>
      ) : null}

      {/* Mobile Timetable Quick View */}
      {ttOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 p-3 md:hidden">
          <div className="mx-auto mt-2 max-w-md rounded-3xl border-2 border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4">
              <div>
                <div className="text-lg font-extrabold tracking-tight text-slate-800">Timetable</div>
                <div className="text-xs text-slate-500">
                  {ttMode === "day"
                    ? `${fmtDayLong(ttDay)} at a glance`
                    : "Weekly overview • rotate phone for best view"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setTtOpen(false)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                ✕
              </button>
            </div>

            <div className="border-b border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTtMode("day")}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-semibold ${ttMode === "day"
                    ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-white text-slate-800"
                    }`}
                >
                  Today
                </button>

                <button
                  type="button"
                  onClick={() => setTtMode("week")}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-semibold ${ttMode === "week"
                    ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-white text-slate-800"
                    }`}
                >
                  Week
                </button>
              </div>

              {ttMode === "day" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {TT_DAYS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setTtDay(d)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${ttDay === d
                        ? "border-sky-300 bg-sky-50 text-sky-900"
                        : "border-slate-200 bg-white text-slate-700"
                        }`}
                    >
                      {fmtDayLong(d)}
                      {currentSchoolDay() === d ? " • Today" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="max-h-[78vh] overflow-auto px-4 py-4">
              {ttLoading && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  {t("class.timetableLoading")}
                </div>
              )}

              {!ttLoading && ttError && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                  {ttError}
                </div>
              )}

              {!ttLoading && !ttError && !ttState && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  {t("class.noTimetable")}
                </div>
              )}

              {!ttLoading && !ttError && ttState && (
                <>
                  {ttMode === "day" ? renderDayStack(ttDay) : renderWeekGrid()}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {ttDesktopOpen && (
        <div className="fixed inset-0 z-50 hidden bg-slate-950/45 p-6 backdrop-blur-sm md:block">
          <div className="mx-auto mt-3 max-w-[1240px] overflow-hidden rounded-[30px] border border-white/70 bg-white/88 shadow-[0_24px_70px_rgba(15,23,42,0.18)] backdrop-blur-xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200/80 bg-gradient-to-r from-white via-emerald-50/70 to-cyan-50/70 px-6 py-4">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700">Quick Reference</div>
                <div className="mt-1 text-2xl font-black tracking-tight text-slate-900">Timetable</div>
                <div className="mt-1 text-xs text-slate-600">
                  Weekly snapshot with your live Teacher Admin colours and layout.
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="hidden lg:flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTtMode("day")}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold ${ttMode === "day"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                      : "border-slate-200 bg-white text-slate-700"
                      }`}
                  >
                    Day
                  </button>
                  <button
                    type="button"
                    onClick={() => setTtMode("week")}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold ${ttMode === "week"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                      : "border-slate-200 bg-white text-slate-700"
                      }`}
                  >
                    Week
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setTtDesktopOpen(false)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="max-h-[82vh] overflow-auto px-5 py-4">
              {ttLoading && (
                <div className="rounded-[26px] border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                  {t("class.timetableLoading")}
                </div>
              )}

              {!ttLoading && ttError && (
                <div className="rounded-[26px] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">
                  {ttError}
                </div>
              )}

              {!ttLoading && !ttError && !ttState && (
                <div className="rounded-[26px] border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                  {t("class.noTimetable")}
                </div>
              )}

              {!ttLoading && !ttError && ttState && (
                <div className="space-y-5">
                  {ttMode === "day" && (
                    <div className="flex flex-wrap gap-2">
                      {TT_DAYS.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setTtDay(d)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${ttDay === d
                            ? "border-sky-300 bg-sky-50 text-sky-900"
                            : "border-slate-200 bg-white text-slate-700"
                            }`}
                        >
                          {fmtDayLong(d)}
                          {currentSchoolDay() === d ? " • Today" : ""}
                        </button>
                      ))}
                    </div>
                  )}

                  {ttMode === "day" ? renderDesktopDayStackBranded(ttDay) : renderDesktopWeekGridBranded()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded-3xl border-2 border-slate-200 bg-white shadow-xl">
            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xl font-extrabold tracking-tight"><UiText translationKey="createClass.title" /></div>
                  <div className="mt-1 text-sm text-slate-600">
                    <UiText translationKey="createClass.help" />
                  </div>
                </div>
                <button className={pill} type="button" onClick={() => setCreateOpen(false)}>
                  {t("common.close")}
                </button>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {customClassMode ? (
                  <>
                    <div>
                      <label className="mb-1 block text-sm font-bold text-slate-700" htmlFor="custom-class-line-one">
                        {t("createClass.customLineOne")}
                      </label>
                      <input
                        id="custom-class-line-one"
                        className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                        value={customClassName}
                        onChange={(e) => setCustomClassName(e.target.value)}
                        placeholder={t("createClass.customLineOnePlaceholder")}
                        maxLength={80}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-bold text-slate-700" htmlFor="custom-class-line-two">
                        {t("createClass.customLineTwo")}
                      </label>
                      <input
                        id="custom-class-line-two"
                        className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                        value={customClassSubject}
                        onChange={(e) => setCustomClassSubject(e.target.value)}
                        placeholder={t("createClass.customLineTwoPlaceholder")}
                        maxLength={80}
                      />
                    </div>
                  </>
                ) : (
                  <>
                <div>
                  <div className="mb-1 text-sm font-bold text-slate-700">{t("createClass.stream")}</div>
                  <select
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                    value={stream}
                    onChange={(e) => {
                      const s = e.target.value as Stream;
                      setStream(s);
                      const years = yearOptionsForStream(s);
                      const y = years[0];
                      setYear(y);
                      const lvls = levelOptionsForStream(s, y);
                      setLevel(lvls[0]);
                    }}
                  >
                    <option value="Junior Cycle">{t("createClass.juniorCycle")}</option>
                    <option value="Senior Cycle">{t("createClass.seniorCycle")}</option>
                    <option value="Transition Year (TY)">{t("createClass.transitionYear")}</option>
                    <option>LCA</option>
                    <option>LCVP</option>
                    <option value="SEN">{t("createClass.sen")}</option>
                    <option>Clubs</option>
                  </select>
                </div>

                <div>
                  <div className="mb-1 text-sm font-bold text-slate-700">{t("createClass.year")}</div>
                  <select
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                    value={year}
                    onChange={(e) => {
                      const y = e.target.value as YearOption;
                      setYear(y);
                      const lvls = levelOptionsForStream(stream, y);
                      setLevel(lvls[0]);
                    }}
                  >
                    {yearOptionsForStream(stream).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="mb-1 text-sm font-bold text-slate-700">{t("createClass.level")}</div>
                  <select
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                    value={level}
                    onChange={(e) => setLevel(e.target.value as LevelOption)}
                  >
                    {levelOptionsForStream(stream, year).map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="mb-1 text-sm font-bold text-slate-700">{t("createClass.subject")}</div>
                  <input
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g. Maths, Physics, English"
                  />
                </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <div className="mb-2 text-sm font-bold text-slate-700">{t("createClass.tileColour")}</div>
                  <div className="grid grid-cols-6 gap-2 md:grid-cols-12">
                    {CLASS_COLOUR_OPTIONS.map((c) => {
                      const selected = pickedColour === c.key;
                      return (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => setPickedColour(c.key)}
                          className={`h-9 w-9 rounded-2xl border-2 border-white ${c.backgroundClass}
                            ring-2 ${selected ? c.ringClass : "ring-transparent"} hover:brightness-110`}
                          title={c.name}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  className="text-sm font-semibold text-emerald-800 underline decoration-emerald-300 underline-offset-4 hover:text-emerald-950"
                  type="button"
                  onClick={() => setCustomClassMode((current) => !current)}
                >
                  {customClassMode ? t("createClass.standardClass") : t("createClass.customClass")}
                </button>
                <div className="flex items-center gap-3">
                <button className={pill} type="button" onClick={() => setCreateOpen(false)}>
                  {t("common.cancel")}
                </button>
                <button
                  className={btnPrimary}
                  type="button"
                  onClick={createClass}
                  disabled={creating || (customClassMode && (!customClassName.trim() || !customClassSubject.trim()))}
                >
                  {creating ? t("dashboard.creatingClass") : t("dashboard.createClass")}
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded-3xl border-2 border-slate-200 bg-white shadow-xl">
            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xl font-extrabold tracking-tight">{t("dashboard.editClass")}</div>
                  <div className="mt-1 text-sm text-slate-600">
                    Update name/subject and choose a tile colour.
                  </div>
                </div>
                <button className={pill} type="button" onClick={() => setEditOpen(false)}>
                  {t("common.close")}
                </button>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-1 text-sm font-bold text-slate-700">{t("createClass.className")}</div>
                  <input
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g. 6th Year Maths"
                  />
                </div>

                <div>
                  <div className="mb-1 text-sm font-bold text-slate-700">{t("createClass.subject")}</div>
                  <input
                    className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    placeholder="e.g. Maths"
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="mb-2 text-sm font-bold text-slate-700">{t("createClass.tileColour")}</div>
                  <div className="grid grid-cols-6 gap-2 md:grid-cols-12">
                    {CLASS_COLOUR_OPTIONS.map((c) => {
                      const selected = editColour === c.key;
                      return (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => setEditColour(c.key)}
                          className={`h-9 w-9 rounded-2xl border-2 border-white ${c.backgroundClass}
                            ring-2 ${selected ? c.ringClass : "ring-transparent"} hover:brightness-110`}
                          title={c.name}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button className={pill} type="button" onClick={() => setEditOpen(false)}>
                  {t("common.cancel")}
                </button>
                <button className={btnPrimary} type="button" onClick={saveEdit} disabled={savingEdit}>
                  {savingEdit ? t("common.saving") : t("common.saveChanges")}
                </button>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Note: name, subject, and tile colour save to your account.
              </div>
            </div>
          </div>
        </div>
      )}

      {manageOpen && manageClass && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4">
          <div className="w-full max-w-xl rounded-3xl border-2 border-slate-200 bg-white shadow-xl">
            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xl font-extrabold tracking-tight">{t("dashboard.manageClass")}</div>
                  <div className="mt-1 text-sm text-slate-600">
                    {t("dashboard.manageClassHelp")}
                  </div>
                </div>
                <button
                  className={pill}
                  type="button"
                  onClick={() => {
                    if (manageBusy) return;
                    setManageOpen(false);
                    setManageClass(null);
                    setManageError(null);
                  }}
                >
                  {t("common.close")}
                </button>
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <span className="font-semibold text-slate-900">{manageClass.name}</span>
                {manageClass.subject ? ` • ${manageClass.subject}` : ""}
              </div>

              {manageError && (
                <InlineNotice
                  variant="error"
                  title="That didn’t quite work"
                  message={manageError}
                  className="mt-4"
                />
              )}

              <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
                <button
                  className={pill}
                  type="button"
                  onClick={() => {
                    if (manageBusy) return;
                    setManageOpen(false);
                    setManageClass(null);
                    setManageError(null);
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button className={btnPrimary} type="button" onClick={archiveSelectedClass} disabled={manageBusy}>
                  {manageBusy ? t("common.loading") : t("dashboard.archiveClass")}
                </button>
                <button
                  className="rounded-2xl border-2 border-rose-600 bg-white px-6 py-3 text-base font-semibold text-rose-700 shadow-sm hover:bg-rose-50 active:translate-y-[1px] disabled:opacity-50"
                  type="button"
                  onClick={deleteSelectedClassPermanently}
                  disabled={manageBusy}
                >
                  {t("dashboard.deletePermanently")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const { t, refreshAccount } = useUiLanguage();
  const [isAuthed, setIsAuthed] = useState(() => !!getToken());
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installedApp, setInstalledApp] = useState(() => isStandaloneApp());
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [isDemoAccount, setIsDemoAccount] = useState(false);
  const [demoRestriction, setDemoRestriction] = useState<string | null>(null);
  const demoRestrictionDialogRef = useRef<HTMLDivElement | null>(null);
  const demoRestrictionReturnFocusRef = useRef<HTMLElement | null>(null);
  const [currentSchoolBrand, setCurrentSchoolBrand] = useState<{ id?: number | null; name?: string | null; logoUrl?: string | null } | null>(null);
  const hasDemoToken = (() => {
    try { return Boolean(sessionStorage.getItem("elume_demo_token")); } catch { return false; }
  })();
  const userEmail = useMemo(() => getEmailFromToken(), [isAuthed]);
  const userLabel = userEmail ?? "";
  const pilotUsers = new Set(["admin@elume.ie", "rob@elume.ie", "emma@elume.ie", "gillian@elume.ie"]);
  const isPilotUser = userEmail ? pilotUsers.has(userEmail.toLowerCase()) : false;
  const location = useLocation();
  const navigate = useNavigate();
  // Dashboard is the root route in App.tsx
  const isDashboard = location.pathname === "/";
  const isBillingRoute =
    location.pathname === "/billing" ||
    location.pathname === "/onboarding/billing" ||
    location.pathname === "/billing/success" ||
    location.pathname === "/billing/cancel";

  useEffect(() => {
    refreshAccount();
  }, [isAuthed, refreshAccount]);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalledApp(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt as EventListener);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt as EventListener);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (!demoRestriction) return;
    demoRestrictionReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = demoRestrictionDialogRef.current;
    const focusable = () => Array.from(
      dialog?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    const focusFirst = () => (focusable()[0] ?? dialog)?.focus();
    const frame = window.requestAnimationFrame(focusFirst);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setDemoRestriction(null);
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) {
        event.preventDefault();
        dialog?.focus();
        return;
      }
      const index = items.indexOf(document.activeElement as HTMLElement);
      const next = event.shiftKey ? (index <= 0 ? items.length - 1 : index - 1) : (index === items.length - 1 ? 0 : index + 1);
      event.preventDefault();
      items[next].focus();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown, true);
      demoRestrictionReturnFocusRef.current?.focus();
      demoRestrictionReturnFocusRef.current = null;
    };
  }, [demoRestriction]);

  useEffect(() => {
    const showRestriction = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string | null }>).detail;
      setDemoRestriction(detail?.message || "This demo lets you explore a prepared class. In your own Elume account, you can make and save changes.");
    };
    window.addEventListener("elume-demo-restricted", showRestriction);
    return () => window.removeEventListener("elume-demo-restricted", showRestriction);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthed) {
      setCurrentRole(null);
      setCurrentSchoolBrand(null);
      setIsDemoAccount(false);
      return;
    }
    void apiFetch("/auth/me")
      .then((data) => {
        if (!cancelled) {
          setCurrentRole(typeof data?.role === "string" ? data.role : null);
          setIsDemoAccount(Boolean(data?.is_demo_account));
          setCurrentSchoolBrand(data?.school_id ? { id: data.school_id, name: data.school_name, logoUrl: data.school_logo_url } : null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // A tab-scoped demo identity must never fall back into the normal
          // localStorage account when it expires. Keep the real login intact
          // and send only this demo tab through its re-entry flow.
          if (hasDemoToken) {
            clearDemoToken();
            setIsAuthed(false);
            navigate("/demo/account", { replace: true });
            return;
          }
          setCurrentRole(null);
          setCurrentSchoolBrand(null);
          setIsDemoAccount(false);
        }
      });
    return () => { cancelled = true; };
  }, [hasDemoToken, isAuthed, navigate]);

  // Public routes should NOT require login
  const isPublicRoute =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/verify-email" ||
    location.pathname === "/demo/cat4-analysis" ||
    location.pathname === "/demo/account" ||
    location.pathname.startsWith("/s/") ||
    location.pathname === "/student" ||
    location.pathname.startsWith("/student/") ||
    location.pathname.startsWith("/join/") ||
    location.pathname.startsWith("/collab/join/") ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/school-invite/") ||
    (location.pathname === "/school-resources" && !isAuthed);

  useEffect(() => {
    if (!isAuthed || isPublicRoute || isBillingRoute || isDemoAccount || hasDemoToken) return;

    let cancelled = false;

    async function checkBilling() {
      try {
        const data = (await apiFetch("/billing/me")) as {
          billing_onboarding_required?: boolean;
          subscription_expired?: boolean;
          requires_billing_redirect?: boolean;
          access_allowed?: boolean;
        };

        if (cancelled) return;

        if (data?.access_allowed !== true && (
          data?.billing_onboarding_required ||
          data?.subscription_expired ||
          data?.requires_billing_redirect
        )) {
          navigate("/onboarding/billing", { replace: true });
        }
      } catch {
        // fail silently and do not block the app shell
      }
    }

    void checkBilling();

    return () => {
      cancelled = true;
    };
  }, [isAuthed, isBillingRoute, isPublicRoute, isDemoAccount, hasDemoToken, navigate]);

  function logout() {
    clearToken();
    setIsAuthed(false);
  }

  if (!isAuthed && location.pathname === "/") {
    return <PublicHomePage />;
  }

  if (!isAuthed && !isPublicRoute) {
    return <LoginPage onLoggedIn={() => setIsAuthed(true)} />;
  }


  return (
    <>
      {/* GLOBAL TOP BAR */}
      {!isPublicRoute && (

        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b bg-white">
          {/* ✅ Logo only (no text) -> Dashboard */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-xl p-1 hover:bg-slate-100"
            title={t("class.backToDashboard")}
          >
            <img src={ELogo2} alt="Elume" className="h-9 w-9 object-contain" />
          </button>
          <div className="flex items-center gap-2">
            {currentRole === "platform_admin" && <button onClick={() => navigate("/platform-admin/schools")} className="rounded-xl border-2 border-violet-200 bg-violet-50 px-3 py-1 text-sm font-semibold text-violet-800 hover:bg-violet-100">{t("nav.platformAdmin")}</button>}
            {currentRole === "platform_admin" && <button onClick={() => navigate("/platform-admin/entitlements")} className="rounded-xl border-2 border-cyan-200 bg-cyan-50 px-3 py-1 text-sm font-semibold text-cyan-800 hover:bg-cyan-100">Access &amp; Billing</button>}
            {currentRole === "school_admin" && <button onClick={() => navigate("/school-admin")} className="rounded-xl border-2 border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-800 hover:bg-emerald-100">{t("nav.schoolAdmin")}</button>}
            {(currentRole === "teacher" || currentRole === "school_admin") && <button onClick={() => navigate("/school-resources")} className="rounded-xl border-2 border-cyan-200 bg-cyan-50 px-3 py-1 text-sm font-semibold text-cyan-800 hover:bg-cyan-100">{t("nav.schoolResources")}</button>}
            <LanguageSwitch />
            <button onClick={logout} className="rounded-xl border-2 border-slate-200 px-4 py-1 font-semibold hover:bg-slate-100">{t("nav.logout")}</button>
          </div>
        </div>
      )}
      {isDemoAccount && !isPublicRoute && !location.pathname.startsWith("/whiteboard/") && (
        <div role="status" className="relative z-50 flex flex-wrap items-center justify-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-950">
          <strong>Demo account</strong><span>Explore a prepared demo class. Start your free 14-day trial.</span>
          <button type="button" onClick={() => window.location.assign("#/register")} className="font-bold underline underline-offset-2">Start a real account</button>
        </div>
      )}
      {demoRestriction && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) event.preventDefault(); }}>
          <div ref={demoRestrictionDialogRef} tabIndex={-1} className="w-full max-w-lg rounded-3xl border border-teal-100 bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="demo-restriction-title" aria-describedby="demo-restriction-message">
            <p className="text-sm font-extrabold uppercase tracking-wide text-teal-700">Elume demo</p>
            <h2 id="demo-restriction-title" className="mt-1 text-2xl font-black text-slate-950">Explore the prepared class</h2>
            <p id="demo-restriction-message" className="mt-3 text-slate-700">{demoRestriction}</p>
            <p className="mt-2 text-sm text-slate-600">{t("demo.restriction.changeHelp")}</p>
            <p className="mt-3 font-semibold text-slate-800">Start your free 14-day trial to try it yourself.</p>
            <div className="mt-5 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => setDemoRestriction(null)} className="rounded-xl border border-slate-300 px-4 py-2 font-bold text-slate-800">Continue exploring</button>
              <button type="button" onClick={() => window.location.assign("#/register")} className="rounded-xl bg-teal-700 px-4 py-2 font-bold text-white">Start my free trial</button>
            </div>
          </div>
        </div>
      )}
      {/* APP ROUTES */}
      <Routes>
        <Route path="/login" element={<LoginPage onLoggedIn={() => setIsAuthed(true)} />} />
        <Route path="/join/:code" element={<StudentJoinQuizPage />} />
        <Route path="/class/:id" element={<ClassPage />} />
        <Route path="/class/:id/seating-plan" element={<SeatingPlanPage />} />
        <Route path="/class/:id/live-quiz" element={<LiveQuizPage />} />
        <Route path="/whiteboard/:id" element={<WhiteBoardPage />} />
        <Route path="/class/:id/notes" element={<NotesPage />} />
        <Route path="/class/:id/exam-papers" element={<ExamPapersPage />} />
        <Route path="/class/:id/videos" element={<VideosPage />} />
        <Route path="/class/:id/links" element={<LinksPage />} />
        <Route path="/class/:id/quizzes" element={<QuizzesPage />} />
        <Route path="/class/:id/tests" element={<Tests />} />
        <Route path="/class/:id/calendar" element={<CalendarPage />} />
        <Route path="/class/:id/admin" element={<ClassAdminPage />} />
        <Route path="/class/:id/admin/cat4" element={<Cat4InsightsPage />} />
        <Route path="/class/:id/admin/aac" element={<ClassAdminPage />} />
        <Route path="/demo/cat4-analysis" element={<Cat4DemoPage />} />
        <Route path="/demo/account" element={<DemoAccountEntryPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/student" element={<StudentPage />} />
        <Route path="/student/practice/library" element={<StudentPracticeLibraryPage />} />
        <Route path="/student/practice/library/:collectionKey" element={<StudentPracticeLibraryPage />} />
        <Route path="/student/practice/library/:collectionKey/:path1" element={<StudentPracticeLibraryPage />} />
        <Route path="/student/practice/library/:collectionKey/:path1/:path2" element={<StudentPracticeLibraryPage />} />
        <Route path="/student/practice/library/:collectionKey/:path1/:path2/:path3" element={<StudentPracticeLibraryPage />} />
        <Route path="/student/practice-quiz/:quizId" element={<StudentPracticeQuizPage />} />
        <Route path="/student/:token/practice-quiz/:quizId" element={<StudentPracticeQuizPage />} />
        <Route path="/student/exam-papers" element={<StudentExamPapersPage />} />
        <Route path="/student/exam-papers/mr-fitz-maths-mini-papers" element={<PublicExamCollectionPage />} />
        <Route path="/student/exam-papers/mr-fitz-maths-mini-papers/algebra" element={<PublicExamTopicPage legacyTopicId="algebra" />} />
        <Route path="/student/exam-papers/mr-fitz-maths-mini-papers/:levelId/:topicId" element={<PublicExamTopicPage />} />
        <Route path="/student/exam-papers/mr-fitz-maths-mini-papers/:levelId" element={<PublicExamLevelPage />} />
        <Route path="/student/:token" element={<StudentClassPage />} />
        <Route path="/s/:token" element={<StudentClassPage />} />
        <Route
          path="/"
          element={<Dashboard installPromptEvent={installPromptEvent} installedApp={installedApp} onInstallPromptConsumed={() => setInstallPromptEvent(null)} />}
        />
        <Route path="/admin" element={<TeacherAdminPage />} />
        <Route path="/admin-users" element={<AdminUsersPage />} />
        <Route path="/school-admin" element={<SchoolAdminPage />} />
        <Route path="/school-resources" element={<SchoolResourcesPage />} />
        <Route path="/platform-admin/schools" element={<PlatformAdminSchoolsPage />} />
        <Route path="/platform-admin/entitlements" element={<PlatformAdminEntitlementsPage />} />
        <Route path="/class/:id/report" element={<ClassReportPage />} />
        <Route path="/class/:id/student-report/:studentId" element={<StudentReportPage />} />
        <Route path="/collab/join/:code" element={<StudentCollabRoomPage />} />
        <Route path="/class/:id/collaboration" element={<CollaborationPage />} />
        <Route path="/planner" element={<TeacherPlanner />} />
        <Route path="/create-resources" element={<CreateResources />} />
        <Route path="/archived-classes" element={<ArchivedClassesPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/school-invite/:token" element={<SchoolInvitationPage />} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="/billing" element={<BillingOnboardingPage />} />
        <Route path="/onboarding/billing" element={<BillingOnboardingPage />} />
      </Routes>

      {!location.pathname.startsWith("/join/") &&
        location.pathname !== "/student" &&
        !location.pathname.startsWith("/student/") &&
        !location.pathname.startsWith("/s/") &&
        !location.pathname.startsWith("/collab/join/") &&
        !location.pathname.startsWith("/reset-password") &&
        !location.pathname.startsWith("/school-invite/") && (
          <footer className="mt-10 border-t border-slate-200/80 bg-white/85 backdrop-blur">
            <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-sm text-slate-600 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
              <div className="text-xs sm:text-sm">© 2026 Elume. All rights reserved.</div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm">
                <Link
                  to="/legal"
                  className="font-medium text-slate-600 transition hover:text-emerald-700 hover:underline underline-offset-4"
                >
                  Legal & Privacy
                </Link>

                <a
                  href="mailto:admin@elume.ie"
                  className="font-medium text-slate-600 transition hover:text-emerald-700 hover:underline underline-offset-4"
                >
                  admin@elume.ie
                </a>
              </div>
            </div>

            <div className="border-t border-slate-100 bg-slate-50/70">
              <div className="mx-auto max-w-7xl px-4 py-3 text-[11px] leading-relaxed text-slate-500 sm:px-6 lg:px-8">
                Elume is operated in Ireland. For legal, privacy, or data queries, contact admin@elume.ie.
              </div>
            </div>
          </footer>
        )}


      {currentRole !== "platform_admin" &&
        currentSchoolBrand?.id &&
        !isPublicRoute &&
        location.pathname !== "/school-admin" && (
          <div className="fixed bottom-3 left-3 z-50 max-w-[calc(50vw-1.25rem)] sm:max-w-[220px]">
            <div className="rounded-xl border border-emerald-100 bg-white/90 px-2.5 py-1.5 shadow-sm backdrop-blur">
              <SchoolBrand compact name={currentSchoolBrand.name} logoUrl={currentSchoolBrand.logoUrl} poweredByElume />
            </div>
          </div>
        )}

      {userEmail && !isPublicRoute && (
        <div className="fixed bottom-3 right-3 z-50 flex flex-col items-end gap-2">
          {isDashboard && isPilotUser && (
            <img
              src={pilotUserBadge}
              alt="Pilot User"
              className="hidden md:block w-[75px] h-auto object-contain drop-shadow-md"
            />
          )}

          <div className="rounded-lg border border-slate-200 bg-white/90 px-2 py-1 text-[10px] leading-tight text-slate-600 shadow-sm">
            <div className="opacity-70">Signed in</div>
            <div className="font-semibold truncate max-w-[140px]">{userEmail}</div>
          </div>
        </div>
      )}
    </>
  );
}
